import { DeviceToolError } from "../errors";
import { readBlobArrayBuffer } from "../files/ChunkedFileReader";

export const SPARSE_MAGIC = 0xed26ff3a;
export const FASTBOOT_SPARSE_SPLIT_SIZE = 128 * 1024 * 1024;
const SPARSE_RAW = 0xcac1;
const SPARSE_FILL = 0xcac2;
const SPARSE_SKIP = 0xcac3;
const SPARSE_CRC32 = 0xcac4;
const SPARSE_FILE_HEADER_SIZE = 28;
const SPARSE_CHUNK_HEADER_SIZE = 12;
const SPARSE_BLOCK_SIZE = 4096;

export interface SparseHeader {
  magic: number;
  majorVersion: number;
  minorVersion: number;
  fileHeaderSize: number;
  chunkHeaderSize: number;
  blockSize: number;
  totalBlocks: number;
  totalChunks: number;
  checksum: number;
  outputSize: number;
}

export interface FastbootSparsePart {
  blob: Blob;
  outputBytes: number;
}

interface SparseChunk {
  type: number;
  blocks: number;
  data: Uint8Array;
}

function readUint32(view: DataView, offset: number): number {
  return view.getUint32(offset, true);
}

export async function parseSparseHeader(file: Blob): Promise<SparseHeader | undefined> {
  if (file.size < 28) return undefined;
  const bytes = await readBlobArrayBuffer(file.slice(0, 28));
  const view = new DataView(bytes);
  const header: SparseHeader = {
    magic: readUint32(view, 0),
    majorVersion: view.getUint16(4, true),
    minorVersion: view.getUint16(6, true),
    fileHeaderSize: view.getUint16(8, true),
    chunkHeaderSize: view.getUint16(10, true),
    blockSize: readUint32(view, 12),
    totalBlocks: readUint32(view, 16),
    totalChunks: readUint32(view, 20),
    checksum: readUint32(view, 24),
    outputSize: readUint32(view, 12) * readUint32(view, 16),
  };
  if (header.magic !== SPARSE_MAGIC) return undefined;
  if (header.majorVersion !== 1 || header.fileHeaderSize < 28 || header.chunkHeaderSize < 12 || header.blockSize === 0) {
    throw new DeviceToolError("INVALID_IMAGE", "Sparse header không hợp lệ.");
  }
  const minimumHeader = header.fileHeaderSize + header.chunkHeaderSize * header.totalChunks;
  if (minimumHeader > file.size) throw new DeviceToolError("INVALID_IMAGE", "Bảng chunk vượt quá kích thước file.");
  return header;
}

export async function inspectSparseImage(file: Blob): Promise<{ sparse: boolean; header?: SparseHeader }> {
  return { sparse: Boolean(await parseSparseHeader(file)), header: await parseSparseHeader(file) };
}

function chunkSize(chunk: SparseChunk): number {
  return SPARSE_CHUNK_HEADER_SIZE + chunk.data.byteLength;
}

function chunksSize(chunks: SparseChunk[]): number {
  return SPARSE_FILE_HEADER_SIZE + chunks.reduce((total, chunk) => total + chunkSize(chunk), 0);
}

function createSparseBlob(header: SparseHeader, chunks: SparseChunk[]): Blob {
  const bytes = new Uint8Array(chunksSize(chunks));
  const view = new DataView(bytes.buffer);
  view.setUint32(0, SPARSE_MAGIC, true);
  view.setUint16(4, 1, true);
  view.setUint16(6, 0, true);
  view.setUint16(8, SPARSE_FILE_HEADER_SIZE, true);
  view.setUint16(10, SPARSE_CHUNK_HEADER_SIZE, true);
  view.setUint32(12, header.blockSize, true);
  view.setUint32(16, header.totalBlocks, true);
  view.setUint32(20, chunks.length, true);
  // Re-splitting changes chunk layout, so do not carry the old CRC.
  view.setUint32(24, 0, true);

  let offset = SPARSE_FILE_HEADER_SIZE;
  for (const chunk of chunks) {
    view.setUint16(offset, chunk.type, true);
    view.setUint16(offset + 2, 0, true);
    view.setUint32(offset + 4, chunk.blocks, true);
    view.setUint32(offset + 8, chunkSize(chunk), true);
    offset += SPARSE_CHUNK_HEADER_SIZE;
    bytes.set(chunk.data, offset);
    offset += chunk.data.byteLength;
  }
  return new Blob([bytes], { type: "application/octet-stream" });
}

function skipChunk(blocks: number): SparseChunk {
  return { type: SPARSE_SKIP, blocks, data: new Uint8Array() };
}

function assertSparseBlockAligned(file: Blob, header: SparseHeader): void {
  if (header.blockSize !== SPARSE_BLOCK_SIZE || file.size === 0) {
    throw new DeviceToolError("INVALID_IMAGE", "Fastboot sparse image phải dùng block 4096 bytes.");
  }
}

async function* splitRawImage(file: Blob, maxSize: number): AsyncGenerator<FastbootSparsePart> {
  if (file.size % SPARSE_BLOCK_SIZE !== 0) {
    throw new DeviceToolError("INVALID_IMAGE", "Raw image phải có kích thước là bội số của 4096 bytes để dùng -S 128M.");
  }
  const totalBlocks = file.size / SPARSE_BLOCK_SIZE;
  const header: SparseHeader = {
    magic: SPARSE_MAGIC,
    majorVersion: 1,
    minorVersion: 0,
    fileHeaderSize: SPARSE_FILE_HEADER_SIZE,
    chunkHeaderSize: SPARSE_CHUNK_HEADER_SIZE,
    blockSize: SPARSE_BLOCK_SIZE,
    totalBlocks,
    totalChunks: 0,
    checksum: 0,
    outputSize: file.size,
  };
  const maxRawBytes = Math.floor((maxSize - SPARSE_FILE_HEADER_SIZE - SPARSE_CHUNK_HEADER_SIZE * 3) / SPARSE_BLOCK_SIZE) * SPARSE_BLOCK_SIZE;
  if (maxRawBytes <= 0) throw new DeviceToolError("UNSUPPORTED_IMAGE", "max-download-size quá nhỏ cho sparse Fastboot.");

  for (let offset = 0; offset < file.size; offset += maxRawBytes) {
    const length = Math.min(maxRawBytes, file.size - offset);
    const data = new Uint8Array(await readBlobArrayBuffer(file.slice(offset, offset + length)));
    const startBlocks = offset / SPARSE_BLOCK_SIZE;
    const endBlocks = startBlocks + length / SPARSE_BLOCK_SIZE;
    const chunks: SparseChunk[] = [];
    if (startBlocks) chunks.push(skipChunk(startBlocks));
    chunks.push({ type: SPARSE_RAW, blocks: length / SPARSE_BLOCK_SIZE, data });
    if (endBlocks < totalBlocks) chunks.push(skipChunk(totalBlocks - endBlocks));
    yield { blob: createSparseBlob(header, chunks), outputBytes: length };
  }
}

async function readSparseChunks(file: Blob, header: SparseHeader): Promise<SparseChunk[]> {
  const chunks: SparseChunk[] = [];
  let offset = header.fileHeaderSize;
  for (let index = 0; index < header.totalChunks; index += 1) {
    const chunkHeader = await readBlobArrayBuffer(file.slice(offset, offset + header.chunkHeaderSize));
    if (chunkHeader.byteLength < SPARSE_CHUNK_HEADER_SIZE) throw new DeviceToolError("INVALID_IMAGE", "Sparse chunk header bị cắt ngắn.");
    const view = new DataView(chunkHeader);
    const type = view.getUint16(0, true);
    const blocks = view.getUint32(4, true);
    const totalSize = view.getUint32(8, true);
    const dataBytes = totalSize - SPARSE_CHUNK_HEADER_SIZE;
    if (![SPARSE_RAW, SPARSE_FILL, SPARSE_SKIP, SPARSE_CRC32].includes(type) || totalSize < SPARSE_CHUNK_HEADER_SIZE || offset + totalSize > file.size) {
      throw new DeviceToolError("INVALID_IMAGE", "Sparse chunk không hợp lệ hoặc bị cắt ngắn.");
    }
    const data = new Uint8Array(await readBlobArrayBuffer(file.slice(offset + SPARSE_CHUNK_HEADER_SIZE, offset + totalSize)));
    if ((type === SPARSE_RAW && dataBytes !== blocks * header.blockSize) || (type === SPARSE_FILL && dataBytes !== 4) || (type === SPARSE_SKIP && dataBytes !== 0) || (type === SPARSE_CRC32 && dataBytes !== 4)) {
      throw new DeviceToolError("INVALID_IMAGE", "Sparse chunk có kích thước dữ liệu không hợp lệ.");
    }
    chunks.push({ type, blocks, data });
    offset += totalSize;
  }
  return chunks;
}

function splitChunk(chunk: SparseChunk, maxDataBytes: number, blockSize: number): SparseChunk[] {
  if (chunk.type !== SPARSE_RAW || chunk.data.byteLength <= maxDataBytes) return [chunk];
  const result: SparseChunk[] = [];
  const blocksPerPart = Math.max(1, Math.floor(maxDataBytes / blockSize));
  for (let offset = 0; offset < chunk.data.byteLength; offset += blocksPerPart * blockSize) {
    const data = chunk.data.slice(offset, Math.min(offset + blocksPerPart * blockSize, chunk.data.byteLength));
    result.push({ type: SPARSE_RAW, blocks: data.byteLength / blockSize, data });
  }
  return result;
}

async function* splitSparseImage(file: Blob, header: SparseHeader, maxSize: number): AsyncGenerator<FastbootSparsePart> {
  const sourceChunks = await readSparseChunks(file, header);
  const chunks: SparseChunk[] = [];
  let coveredBlocks = 0;
  let outputBytes = 0;
  const maxDataBytes = Math.max(SPARSE_BLOCK_SIZE, maxSize - SPARSE_FILE_HEADER_SIZE - SPARSE_CHUNK_HEADER_SIZE * 3);

  const finish = (includeTail: boolean): FastbootSparsePart => {
    const partChunks = [...chunks];
    if (includeTail && coveredBlocks < header.totalBlocks) partChunks.push(skipChunk(header.totalBlocks - coveredBlocks));
    return { blob: createSparseBlob(header, partChunks), outputBytes };
  };

  for (const sourceChunk of sourceChunks) {
    for (const chunk of splitChunk(sourceChunk, maxDataBytes, header.blockSize)) {
      const needsPrefix = chunks.length === 0 && coveredBlocks > 0;
      const candidate = needsPrefix ? [skipChunk(coveredBlocks), chunk] : [...chunks, chunk];
      const candidateSize = chunksSize(candidate) + SPARSE_CHUNK_HEADER_SIZE;
      if (chunks.length > 0 && candidateSize > maxSize) {
        yield finish(true);
        chunks.length = 0;
        outputBytes = 0;
        chunks.push(skipChunk(coveredBlocks), chunk);
      } else {
        if (needsPrefix) chunks.push(skipChunk(coveredBlocks));
        chunks.push(chunk);
      }
      coveredBlocks += chunk.blocks;
      if (chunk.type !== SPARSE_SKIP) outputBytes += chunk.blocks * header.blockSize;
    }
  }
  if (chunks.length > 0) yield finish(false);
}

/**
 * Implements the host-side `fastboot -S 128M` behavior for WebUSB. Large raw
 * images are converted into sparse payloads; existing sparse images are
 * re-split while preserving their logical offsets with SKIP chunks.
 */
export async function* splitFastbootImage(file: Blob, maxSize = FASTBOOT_SPARSE_SPLIT_SIZE): AsyncGenerator<FastbootSparsePart> {
  const header = await parseSparseHeader(file);
  if (!header) {
    yield* splitRawImage(file, maxSize);
    return;
  }
  assertSparseBlockAligned(file, header);
  yield* splitSparseImage(file, header, maxSize);
}
